/**
 * BuildFreshnessCheck
 *
 * Eliminates "old UI after deploy" without requiring server-side cache
 * header changes (LIVE sends `cache-control: public, max-age=300` on HTML,
 * which we can't control from client code).
 *
 * Strategy:
 * - On mount and every 60s afterwards, fetch `/build.json?t=<now>` (the
 *   cache-buster query string forces the browser + CDN to serve the fresh
 *   copy every time).
 * - Compare its `buildId` with the one baked into this JS bundle at build
 *   time (via `BUILD_INFO.hash`).
 * - If they differ, show a sticky, user-dismissible toast:
 *     "Uus versioon saadaval → Laadi uuesti"
 *   with a Reload button. We DO NOT force-reload silently — the user is in
 *   control. Once shown for a given new buildId, we don't spam them again
 *   (sessionStorage guard).
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/lib/context';
import { BUILD_INFO } from '@/buildInfo';

const POLL_INTERVAL_MS = 60_000;
const TOAST_SHOWN_KEY = 'keikka_build_toast_shown_for';

const LABELS = {
  et: { title: 'Uus versioon saadaval', action: 'Laadi uuesti' },
  en: { title: 'New version available', action: 'Reload' },
  fi: { title: 'Uusi versio saatavilla', action: 'Lataa uudelleen' },
  nb: { title: 'Ny versjon tilgjengelig', action: 'Last på nytt' },
  ru: { title: 'Доступна новая версия', action: 'Обновить' },
};

const BuildFreshnessCheck = () => {
  const { language } = useLanguage();
  const toastShownRef = useRef(false);

  useEffect(() => {
    // Log current build for ops visibility.
    // eslint-disable-next-line no-console
    console.log(
      `%cKEIKKA BUILD: ${BUILD_INFO.hash} · ${BUILD_INFO.time}`,
      'background:#10b981;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold'
    );

    const hardReload = () => {
      // Cache-buster on the URL forces browsers/CDNs to fetch fresh HTML.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('_r', Date.now().toString());
        window.location.replace(url.toString());
      } catch (_) {
        window.location.reload();
      }
    };

    const showToast = (newBuildId) => {
      if (toastShownRef.current) return false;
      // Politeness: don't interrupt if the user is currently typing in a form field.
      // Re-checking on the next interval will surface the toast shortly after.
      const ae = typeof document !== 'undefined' ? document.activeElement : null;
      if (ae) {
        const tag = (ae.tagName || '').toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable) {
          return false;
        }
      }
      toastShownRef.current = true;
      const l = LABELS[language] || LABELS.en;
      toast(l.title, {
        description: `→ ${newBuildId}`,
        duration: Infinity,
        action: {
          label: l.action,
          onClick: hardReload,
        },
        dismissible: true,
      });
      return true;
    };

    const check = async () => {
      try {
        const res = await fetch(`/build.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'cache-control': 'no-cache' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data || !data.buildId) return;

        if (data.buildId !== BUILD_INFO.hash) {
          const alreadyShown = sessionStorage.getItem(TOAST_SHOWN_KEY);
          if (alreadyShown === data.buildId) return;
          // eslint-disable-next-line no-console
          console.warn(
            `[build-freshness] new deploy detected (shell=${BUILD_INFO.hash} live=${data.buildId}) — prompting user to reload`
          );
          const shown = showToast(data.buildId);
          if (shown) {
            sessionStorage.setItem(TOAST_SHOWN_KEY, data.buildId);
          }
        }
      } catch (_) {
        /* offline / network glitch — ignore */
      }
    };

    check();
    const timer = setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [language]);

  return null;
};

export default BuildFreshnessCheck;
